import { getGetPhoneStatusQueryKey } from '@biasafe.queries/phone-status/phone-status';
import { getListUnmatchedPhoneCallsQueryKey } from '@biasafe.queries/phone-calls-unmatched/phone-calls-unmatched';
import { getListClientCallsQueryKey } from '@biasafe.queries/clients-client-id-calls/clients-client-id-calls';
import { getGetClientQueryKey } from '@biasafe.queries/clients-client-id/clients-client-id';
import { getListClientsQueryKey } from '@biasafe.queries/clients/clients';
import type { QueryClient } from '@tanstack/react-query';
import { invalidateClientActivity } from '@/features/Clients/components/activityFeed/useClientActivity';

/** The firm's connection changed (connected, disconnected, polled). */
export function invalidatePhoneStatus(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: getGetPhoneStatusQueryKey(),
  });
}

/**
 * Calls moved: a poll stored some, or one was linked to a client.
 *
 * EVERYTHING THAT SHOWS A CALL, because a linked call appears in three places and
 * leaves a fourth: the unmatched inbox loses it, the client's Calls tab gains it,
 * the client's Activity feed gains the timeline entry the link wrote, and the
 * directory's Last Contact moves. `clientId` is omitted after a poll, when the
 * affected clients are not known, and every client's calls are refetched.
 */
export async function invalidatePhoneCalls(
  queryClient: QueryClient,
  clientId?: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: getListUnmatchedPhoneCallsQueryKey(),
    }),
    clientId
      ? queryClient.invalidateQueries({
          queryKey: getListClientCallsQueryKey(clientId),
        })
      : queryClient.invalidateQueries({
          predicate: (q) =>
            typeof q.queryKey[0] === 'string' &&
            /^\/clients\/[^/]+\/calls$/.test(q.queryKey[0]),
        }),
    queryClient.invalidateQueries({ queryKey: getListClientsQueryKey() }),
    ...(clientId
      ? [
          queryClient.invalidateQueries({
            queryKey: getGetClientQueryKey(clientId),
          }),
          invalidateClientActivity(queryClient, clientId),
        ]
      : []),
  ]);
}
